import React, { useState, useEffect, useRef } from "react";
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "../ui/command";
import { Input } from "../ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Trash2 } from "lucide-react";
import { getEquipmentSuggestions } from "services/masterDataService";

export default function AddEquipments({ value = [], names = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState("");

  // Initialize map from props so names persist on re-renders
  const [selectedMap, setSelectedMap] = useState(() => {
    const initialMap = {};
    value.forEach((id, index) => {
      initialMap[id] = names[index] || "Equipment Not Found";
    });
    return initialMap;
  });

  const debounceRef = useRef(null);
  const inputRef = useRef(null); // Ref to hold the input element

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await getEquipmentSuggestions({ query: trimmed });
        const list = res?.data?.data ?? res?.data ?? [];

        const uniqueSuggestions = list.filter(
          (item) => !value.includes(item.equipment_id)
        );

        setSuggestions(uniqueSuggestions);
        setOpen(uniqueSuggestions.length > 0);
      } catch (err) {
        console.error(err);
        setSuggestions([]);
        setOpen(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue, value]);

  const handleSelectSuggestion = (equipment) => {
    const id = equipment.equipment_id;
    const name = equipment.equipment_name;

    if (!value.includes(id)) {
      const newIds = [...value, id];
      const newNames = [...names, name];

      setSelectedMap((prev) => ({ ...prev, [id]: name }));
      // Pass both arrays back to parent
      onChange(newIds, newNames);
    }

    setInputValue("");
    setSuggestions([]);
    setOpen(false);

    // Return focus to input after selection
    inputRef.current?.focus();
  };

  const removeEquipment = (idToRemove) => {
    const index = value.indexOf(idToRemove);
    if (index > -1) {
      const newIds = value.filter((id) => id !== idToRemove);
      const newNames = names.filter((_, i) => i !== index);

      setSelectedMap((prev) => {
        const copy = { ...prev };
        delete copy[idToRemove];
        return copy;
      });

      // Pass both updated arrays back to parent
      onChange(newIds, newNames);
    }
  };

  return (
    <div className="w-full">
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!inputValue.trim()) return;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Input
            ref={inputRef} // Attach the ref here
            type="text"
            placeholder="Please type the equipment name to search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            autoComplete="off"
          />
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-full"
          align="start"
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {/* Note: If your Command component is from shadcn/ui, it uses cmdk.
              Adding loop and preventing manual focus helps keep the cursor in the input.
          */}
          <Command loop={false}>
           <CommandList>
              <CommandEmpty>No equipment found.</CommandEmpty>
              {suggestions.map((equipment) => (
                <CommandItem
                  key={equipment.equipment_id}
                  onSelect={() => handleSelectSuggestion(equipment)}
                  className="aria-selected:bg-transparent hover:aria-selected:bg-accent"
                >
                  {equipment.equipment_name}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex flex-col gap-3 mt-4">
        {value.map((id, i) => (
          <div
            key={`${id}_${i}`}
            className="border border-ring rounded-lg px-3 py-2 flex justify-between items-center text-sm shadow-xs"
          >
            <span>{selectedMap[id] || names[i] || "Equipment Not Found"}</span>
            <Trash2
              className="cursor-pointer text-red-500 w-4 h-4"
              onClick={() => removeEquipment(id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
